import { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useProfile } from '@/hooks/useProfile';
import { useAuth } from '@/hooks/useAuth';
import ProfileHeader from '@/components/Profile/ProfileHeader';
import ProfileGrid from '@/components/Profile/ProfileGrid';
import PetCard from '@/components/Profile/PetCard';
import CreatePetModal from '@/components/Profile/CreatePetModal';
import CreatePostModal from '@/components/Profile/CreatePostModal';
import ProfileSetup from '@/components/Profile/ProfileSetup';
import EditPetModal from '@/components/Profile/EditPetModal';
import AdoptionPostCard from '@/components/Profile/AdoptionPostCard';
import AdoptionRequestsModal from '@/components/Profile/AdoptionRequestsModal';
import SavedAdoptionsModal from '@/components/Profile/SavedAdoptionsModal';
import CreateAdoptionModal from '@/components/Profile/CreateAdoptionModal';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { HeroButton } from '@/components/ui/button-variants';
import { Plus, Grid3X3, Heart, Camera, Bookmark, ThumbsUp, Users, Home, Mail } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from '@/contexts/TranslationContext';
import profileHeroBg from '@/assets/profile-hero-bg.jpg';

const Profile = () => {
  const { user, loading: authLoading } = useAuth();
  const { profile, pets, posts, loading, profileExists, updateProfile, refetchPets, refetchPosts, refetchProfile } = useProfile();
  
  // Set document title and meta description for SEO
  useEffect(() => {
    document.title = 'Il Mio Profilo - Gestisci Pet e Post | AstroPet';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Gestisci il tuo profilo su AstroPet. Aggiungi i tuoi pet, crea post, gestisci adozioni e interagisci con la community degli amanti degli animali. Personalizza la tua esperienza pet.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Gestisci il tuo profilo su AstroPet. Aggiungi i tuoi pet, crea post, gestisci adozioni e interagisci con la community degli amanti degli animali. Personalizza la tua esperienza pet.';
      document.head.appendChild(meta);
    }
    
    // Set canonical URL
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', 'https://astropet.it/profile');
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = 'https://astropet.it/profile';
      document.head.appendChild(link);
    }
  }, []);
  const { toast } = useToast();
  const { t } = useTranslation();
  const [createPetOpen, setCreatePetOpen] = useState(false);
  const [createPostOpen, setCreatePostOpen] = useState(false);
  const [editPetOpen, setEditPetOpen] = useState(false);
  const [selectedPet, setSelectedPet] = useState<any>(null);
  const [createAdoptionOpen, setCreateAdoptionOpen] = useState(false);
  const [editAdoptionPost, setEditAdoptionPost] = useState<any>(null);
  const [adoptionPosts, setAdoptionPosts] = useState<any[]>([]);
  const [requestsModalOpen, setRequestsModalOpen] = useState(false);
  const [selectedAdoptionPost, setSelectedAdoptionPost] = useState<any>(null);
  const [likedPosts, setLikedPosts] = useState<any[]>([]);
  const [savedPosts, setSavedPosts] = useState<any[]>([]);
  const [savedAdoptionPosts, setSavedAdoptionPosts] = useState<any[]>([]);
  const [loadingLiked, setLoadingLiked] = useState(false);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [loadingSavedAdoptions, setLoadingSavedAdoptions] = useState(false);
  const [savedAdoptionsModalOpen, setSavedAdoptionsModalOpen] = useState(false);

  // Fetch adoption posts
  useEffect(() => {
    if (user) {
      fetchAdoptionPosts();
      fetchLikedPosts();
      fetchSavedPosts();
      fetchSavedAdoptionPosts();
    }
  }, [user]);

  const fetchAdoptionPosts = async () => {
    if (!user) return;

    try {
      // Get adoption posts with request counts
      const { data, error } = await supabase
        .from('adoption_posts')
        .select(`
          *,
          requests_count:adoption_requests(count)
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Format the data to include proper request counts
      const formattedPosts = (data || []).map(post => ({
        ...post,
        requests_count: Array.isArray(post.requests_count) ? post.requests_count.length : (post.requests_count || 0)
      }));
      
      setAdoptionPosts(formattedPosts);
    } catch (error) {
      console.error('Error fetching adoption posts:', error);
    }
  };

  const handleTogglePause = async (id: string, currentStatus: string) => {
    try {
      const newStatus = currentStatus === 'published' ? 'paused' : 'published';
      const { error } = await supabase
        .from('adoption_posts')
        .update({ status: newStatus })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: newStatus === 'published' ? "Annuncio riattivato" : "Annuncio messo in pausa",
        description: "Lo stato dell'annuncio è stato aggiornato",
      });

      fetchAdoptionPosts();
    } catch (error) {
      console.error('Error updating post status:', error);
      toast({
        title: "Errore",
        description: "Impossibile aggiornare lo stato dell'annuncio",
        variant: "destructive",
      });
    }
  };

  const handleClosePost = async (id: string) => {
    try {
      const { error } = await supabase
        .from('adoption_posts')
        .update({ status: 'closed' })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Annuncio chiuso",
        description: "L'annuncio è stato chiuso con successo",
      });

      fetchAdoptionPosts();
    } catch (error) {
      console.error('Error closing post:', error);
      toast({
        title: "Errore",
        description: "Impossibile chiudere l'annuncio",
        variant: "destructive",
      });
    }
  };

  const handleDeletePost = async (id: string) => {
    try {
      const { error } = await supabase
        .from('adoption_posts')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Bozza eliminata",
        description: "La bozza è stata eliminata con successo",
      });

      fetchAdoptionPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      toast({
        title: "Errore",
        description: "Impossibile eliminare la bozza",
        variant: "destructive",
      });
    }
  };

  const fetchLikedPosts = async () => {
    if (!user) return;
    
    setLoadingLiked(true);
    try {
      // Get liked post IDs
      const { data: likes, error } = await supabase
        .from('post_likes')
        .select('post_id')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (likes && likes.length > 0) {
        const postIds = likes.map(like => like.post_id);
        
        // Get the actual posts with their details
        const { data: postsData, error: postsError } = await supabase
          .from('posts')
          .select(`
            *,
            pets (name, avatar_url)
          `)
          .in('id', postIds)
          .eq('is_public', true);

        if (postsError) throw postsError;

        // Get profiles for the posts
        const userIds = Array.from(new Set(postsData?.map((p: any) => p.user_id).filter(Boolean))) as string[];
        let profilesMap: Record<string, any> = {};
        
        if (userIds.length > 0) {
          const { data: profilesData } = await supabase
            .from('profiles')
            .select('user_id, username, display_name, avatar_url')
            .in('user_id', userIds);
            
          profilesMap = Object.fromEntries(
            (profilesData || []).map((p: any) => [p.user_id, {
              username: p.username,
              display_name: p.display_name,
              avatar_url: p.avatar_url
            }])
          );
        }

        const postsWithProfiles = (postsData || []).map((p: any) => ({
          ...p,
          profiles: profilesMap[p.user_id] || null
        }));

        setLikedPosts(postsWithProfiles);
      } else {
        setLikedPosts([]);
      }
    } catch (error) {
      console.error('Error fetching liked posts:', error);
      setLikedPosts([]);
    } finally {
      setLoadingLiked(false);
    }
  };

  const fetchSavedPosts = async () => {
    if (!user) return;
    
    setLoadingSaved(true);
    try {
      // Get saved post IDs
      const { data: saves, error } = await supabase
        .from('post_saves')
        .select('post_id')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (saves && saves.length > 0) {
        const postIds = saves.map(save => save.post_id);
        
        // Get the actual posts with their details
        const { data: postsData, error: postsError } = await supabase
          .from('posts')
          .select(`
            *,
            pets (name, avatar_url)
          `)
          .in('id', postIds)
          .eq('is_public', true);

        if (postsError) throw postsError;

        // Get profiles for the posts
        const userIds = Array.from(new Set(postsData?.map((p: any) => p.user_id).filter(Boolean))) as string[];
        let profilesMap: Record<string, any> = {};
        
        if (userIds.length > 0) {
          const { data: profilesData } = await supabase
            .from('profiles')
            .select('user_id, username, display_name, avatar_url')
            .in('user_id', userIds);
            
          profilesMap = Object.fromEntries(
            (profilesData || []).map((p: any) => [p.user_id, {
              username: p.username,
              display_name: p.display_name,
              avatar_url: p.avatar_url
            }])
          );
        }

        const postsWithProfiles = (postsData || []).map((p: any) => ({
          ...p,
          profiles: profilesMap[p.user_id] || null
        }));

        setSavedPosts(postsWithProfiles);
      } else {
        setSavedPosts([]);
      }
    } catch (error) {
      console.error('Error fetching saved posts:', error);
      setSavedPosts([]);
    } finally {
      setLoadingSaved(false);
    }
  };

  const fetchSavedAdoptionPosts = async () => {
    if (!user) return;
    
    setLoadingSavedAdoptions(true);
    try {
      // Get saved adoption post IDs from the correct table
      const { data: saves, error } = await supabase
        .from('adoption_saves')
        .select('adoption_post_id')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (saves && saves.length > 0) {
        const adoptionPostIds = saves.map(save => save.adoption_post_id);
        
        // Get the actual adoption posts with their details
        const { data: adoptionData, error: adoptionError } = await supabase
          .from('adoption_posts')
          .select('*')
          .in('id', adoptionPostIds)
          .eq('status', 'published');

        if (adoptionError) throw adoptionError;

        // Then get profiles for each user
        const userIds = adoptionData?.map(post => post.user_id) || [];
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('user_id, username, display_name, avatar_url')
          .in('user_id', userIds);

        if (profilesError) throw profilesError;

        // Combine the data
        const adoptionPostsData = adoptionData?.map(post => ({
          ...post,
          profiles: profilesData?.find(profile => profile.user_id === post.user_id)
        })) || [];

        

        // Get profiles for the adoption posts
        const adoptionUserIds = Array.from(new Set(adoptionPostsData?.map((p: any) => p.user_id).filter(Boolean))) as string[];
        let profilesMap: Record<string, any> = {};
        
        if (adoptionUserIds.length > 0) {
          const { data: adoptionProfilesData } = await supabase
            .from('profiles')
            .select('user_id, username, display_name, avatar_url')
            .in('user_id', adoptionUserIds);
            
          profilesMap = Object.fromEntries(
            (adoptionProfilesData || []).map((p: any) => [p.user_id, {
              username: p.username,
              display_name: p.display_name,
              avatar_url: p.avatar_url
            }])
          );
        }

        const adoptionPostsWithProfiles = (adoptionPostsData || []).map((p: any) => ({
          ...p,
          profiles: profilesMap[p.user_id] || null
        }));

        setSavedAdoptionPosts(adoptionPostsWithProfiles);
      } else {
        setSavedAdoptionPosts([]);
      }
    } catch (error) {
      console.error('Error fetching saved adoption posts:', error);
      setSavedAdoptionPosts([]);
    } finally {
      setLoadingSavedAdoptions(false);
    }
  };

  // Redirect to auth if not authenticated
  if (!authLoading && !user) {
    return <Navigate to="/auth" replace />;
  }

  // Show profile setup if profile doesn't exist
  if (!loading && profileExists === false) {
    return <ProfileSetup onComplete={refetchProfile} />;
  }

  if (authLoading || loading) {
    return (
      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Profile Header Skeleton */}
        <Card className="glass border-white/20">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="w-32 h-32 bg-muted rounded-full animate-pulse" />
              <div className="flex-1 space-y-4">
                <div className="space-y-2">
                  <div className="h-8 bg-muted rounded w-48 animate-pulse" />
                  <div className="h-4 bg-muted rounded w-32 animate-pulse" />
                </div>
                <div className="flex gap-6">
                  <div className="text-center space-y-1">
                    <div className="h-6 w-12 bg-muted rounded animate-pulse" />
                    <div className="h-4 w-16 bg-muted rounded animate-pulse" />
                  </div>
                  <div className="text-center space-y-1">
                    <div className="h-6 w-12 bg-muted rounded animate-pulse" />
                    <div className="h-4 w-16 bg-muted rounded animate-pulse" />
                  </div>
                  <div className="text-center space-y-1">
                    <div className="h-6 w-12 bg-muted rounded animate-pulse" />
                    <div className="h-4 w-16 bg-muted rounded animate-pulse" />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Content Skeleton */}
        <div className="grid grid-cols-3 gap-2">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="aspect-square bg-muted rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="text-center py-16">
          <Camera className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Profilo non trovato</h3>
          <p className="text-muted-foreground">
            Non è stato possibile caricare il profilo.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="cosmic-background min-h-screen"
      style={{ backgroundImage: `url(${profileHeroBg})` }}
    >
      <div className="max-w-4xl mx-auto p-4 pb-20 lg:pb-4">
      {/* Profile Header */}
      <ProfileHeader 
        profile={profile} 
        isOwnProfile={true}
        onUpdateProfile={updateProfile}
        refetchProfile={refetchProfile}
      />

      {/* Content Tabs */}
      <Tabs defaultValue="posts" className="space-y-6">
        <div className="w-full overflow-x-auto">
          <TabsList className="w-max sm:w-full glass border-white/20 flex-nowrap px-2">
            <TabsTrigger value="posts" className="flex-shrink-0 sm:flex-1 data-[state=active]:bg-primary whitespace-nowrap px-3">
              <Grid3X3 className="w-4 h-4 mr-2" />
              Post ({posts.length})
            </TabsTrigger>
            <TabsTrigger value="pets" className="flex-shrink-0 sm:flex-1 data-[state=active]:bg-primary whitespace-nowrap px-3">
              <Heart className="w-4 h-4 mr-2" />
              Pet ({pets.length})
            </TabsTrigger>
            <TabsTrigger value="adoption" className="flex-shrink-0 sm:flex-1 data-[state=active]:bg-primary whitespace-nowrap px-3">
              <Home className="w-4 h-4 mr-2" />
              Adozione ({adoptionPosts.length})
            </TabsTrigger>
            <TabsTrigger value="liked" className="flex-shrink-0 sm:flex-1 data-[state=active]:bg-primary whitespace-nowrap px-3">
              <ThumbsUp className="w-4 h-4 mr-2" />
              Mi Piace
            </TabsTrigger>
            <TabsTrigger value="saved" className="flex-shrink-0 sm:flex-1 data-[state=active]:bg-primary whitespace-nowrap px-3">
              <Bookmark className="w-4 h-4 mr-2" />
              Salvati
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Posts Tab */}
        <TabsContent value="posts" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">I tuoi Post</h2>
            <Button 
              onClick={() => setCreatePostOpen(true)}
              size="sm"
              className="bg-primary hover:bg-primary/90"
            >
              <Plus className="w-4 h-4 mr-2" />
              Crea Post
            </Button>
          </div>
          
          {posts.length === 0 ? (
            <Card className="glass border-white/20">
              <CardContent className="p-8 text-center">
                <Camera className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Condividi il tuo primo post!</h3>
                <p className="text-muted-foreground mb-6">
                  Mostra al mondo i momenti speciali con i tuoi pet
                </p>
                <HeroButton onClick={() => setCreatePostOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Crea Post
                </HeroButton>
              </CardContent>
            </Card>
          ) : (
            <ProfileGrid posts={posts} />
          )}
        </TabsContent>

        {/* Pets Tab */}
        <TabsContent value="pets" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">I tuoi Pet</h2>
            <Button 
              onClick={() => setCreatePetOpen(true)}
              size="sm"
              className="bg-primary hover:bg-primary/90"
            >
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi Pet
            </Button>
          </div>
          
          {pets.length === 0 ? (
            <Card className="glass border-white/20">
              <CardContent className="p-8 text-center">
                <Heart className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Aggiungi il tuo primo pet!</h3>
                <p className="text-muted-foreground mb-6">
                  Crea un profilo per i tuoi amici a quattro zampe
                </p>
                <HeroButton onClick={() => setCreatePetOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Aggiungi Pet
                </HeroButton>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {pets.map((pet) => (
                <PetCard 
                  key={pet.id} 
                  pet={pet}
                  onEdit={(pet) => {
                    setSelectedPet(pet);
                    setEditPetOpen(true);
                  }}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Adoption Tab */}
        <TabsContent value="adoption" className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <h2 className="text-lg font-semibold">{t('adoption.title')}</h2>
            
            {/* Mobile: Horizontal scroll, Desktop: Normal flex */}
            <div className="w-full overflow-x-auto sm:w-auto sm:overflow-visible">
              <div className="flex gap-2 min-w-max sm:min-w-0 p-1">
                <Button 
                  onClick={() => setSavedAdoptionsModalOpen(true)}
                  variant="outline"
                  size="sm"
                  className="whitespace-nowrap flex-shrink-0"
                >
                  <Bookmark className="w-4 h-4 mr-2" />
                  {t('adoption.saved')} ({savedAdoptionPosts.length})
                </Button>
                <Link to="/app/adoption-requests">
                  <Button 
                    variant="outline"
                    size="sm"
                    className="whitespace-nowrap flex-shrink-0"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    {t('adoption.requests')}
                  </Button>
                </Link>
                <Button 
                  onClick={() => setCreateAdoptionOpen(true)}
                  size="sm"
                  className="bg-primary hover:bg-primary/90 whitespace-nowrap flex-shrink-0"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('adoption.create')}
                </Button>
              </div>
            </div>
          </div>
          
          {adoptionPosts.length === 0 ? (
            <Card className="glass border-white/20">
              <CardContent className="p-8 text-center">
                <Home className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Nessun annuncio di adozione</h3>
                <p className="text-muted-foreground mb-6">
                  Crea il tuo primo annuncio per aiutare un pet a trovare casa
                </p>
                <HeroButton onClick={() => setCreateAdoptionOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Crea Annuncio
                </HeroButton>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {adoptionPosts.map((post) => (
                <AdoptionPostCard 
                  key={post.id} 
                  post={post}
                  onEdit={(post) => {
                    setEditAdoptionPost(post);
                    setCreateAdoptionOpen(true);
                  }}
                  onTogglePause={handleTogglePause}
                  onViewRequests={(id) => {
                    const post = adoptionPosts.find(p => p.id === id);
                    if (post) {
                      setSelectedAdoptionPost(post);
                      setRequestsModalOpen(true);
                    }
                  }}
                  onClose={handleClosePost}
                  onDelete={handleDeletePost}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Liked Posts Tab */}
        <TabsContent value="liked" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Post che ti Piacciono</h2>
            <Button 
              onClick={fetchLikedPosts}
              variant="outline"
              size="sm"
              disabled={loadingLiked}
            >
              {loadingLiked ? 'Caricamento...' : 'Aggiorna'}
            </Button>
          </div>
          
          {loadingLiked ? (
            <div className="grid grid-cols-3 gap-2">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="aspect-square bg-muted rounded animate-pulse" />
              ))}
            </div>
          ) : likedPosts.length === 0 ? (
            <Card className="glass border-white/20">
              <CardContent className="p-8 text-center">
                <ThumbsUp className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Nessun post piaciuto</h3>
                <p className="text-muted-foreground mb-6">
                  I post che ti piacciono appariranno qui
                </p>
                <Link to="/app/feed">
                  <HeroButton>
                    <Heart className="w-4 h-4 mr-2" />
                    Esplora il Feed
                  </HeroButton>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <ProfileGrid posts={likedPosts} />
          )}
        </TabsContent>

        {/* Saved Posts Tab */}
        <TabsContent value="saved" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Post Salvati</h2>
            <Button 
              onClick={fetchSavedPosts}
              variant="outline"
              size="sm"
              disabled={loadingSaved}
            >
              {loadingSaved ? 'Caricamento...' : 'Aggiorna'}
            </Button>
          </div>
          
          {loadingSaved ? (
            <div className="grid grid-cols-3 gap-2">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="aspect-square bg-muted rounded animate-pulse" />
              ))}
            </div>
          ) : savedPosts.length === 0 ? (
            <Card className="glass border-white/20">
              <CardContent className="p-8 text-center">
                <Bookmark className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Nessun post salvato</h3>
                <p className="text-muted-foreground mb-6">
                  Salva i post che ti interessano per rivederli più tardi
                </p>
                <Link to="/app/feed">
                  <HeroButton>
                    <Camera className="w-4 h-4 mr-2" />
                    Scopri Contenuti
                  </HeroButton>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <ProfileGrid posts={savedPosts} />
          )}
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <EditPetModal
        open={editPetOpen}
        onOpenChange={setEditPetOpen}
        pet={selectedPet}
        onSuccess={() => {
          refetchPets();
          refetchProfile();
          setSelectedPet(null);
        }}
      />
      
      <CreatePetModal
        open={createPetOpen} 
        onOpenChange={setCreatePetOpen}
          onSuccess={() => {
            refetchPets();
            refetchPosts();
          }}
      />
      
      <CreatePostModal
        open={createPostOpen}
        onOpenChange={setCreatePostOpen}
        onSuccess={() => {
          refetchPosts();
          refetchPets();
        }}
      />
      
      <CreateAdoptionModal
        open={createAdoptionOpen}
        onOpenChange={(open) => {
          setCreateAdoptionOpen(open);
          if (!open) {
            setEditAdoptionPost(null);
          }
        }}
        existingPost={editAdoptionPost}
        onSuccess={() => {
          fetchAdoptionPosts();
        }}
      />
      
      <AdoptionRequestsModal
        open={requestsModalOpen}
        onOpenChange={setRequestsModalOpen}
        adoptionPostId={selectedAdoptionPost?.id || ''}
        petName={selectedAdoptionPost?.pet_name || ''}
        onRequestUpdate={fetchAdoptionPosts}
      />
      
      <SavedAdoptionsModal
        open={savedAdoptionsModalOpen}
        onOpenChange={setSavedAdoptionsModalOpen}
        savedAdoptions={savedAdoptionPosts}
        onRefresh={fetchSavedAdoptionPosts}
      />
      </div>
    </div>
  );
};

export default Profile;